import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Product } from '../types/product';
import { CartItem, CartState, CartContextType, AddItemMeta, TaxConfig } from '../types/cart';
import { AddToCartModal } from '../components/cart/AddToCartModal';
import { facebookPixel } from '../utils/facebookPixel';
import api from '../services/api';
import { tenantStorage } from '../utils/storage';
import {
  applyBundleOffers,
  getBundleOffersSync,
  loadBundleOffers,
  type BundleOffer,
} from '../utils/bundleOffers';

const CART_STORAGE_KEY = 'diviteb_cart';

/**
 * Module-scope cache for tax config, keyed by tenant domain.
 *
 * The tax config rarely changes (set in admin once per tenant). Previously
 * every CartProvider mount fired a `/tenant/tax-config` request — and the
 * provider re-mounts on every route nav, so a single shopper added a
 * handful of identical requests per session for no benefit.
 *
 * Cache lives for the lifetime of the SPA tab. No TTL because:
 *   - Tax changes require admin action which would be followed by a
 *     hard-refresh on the merchant side anyway.
 *   - On the shopper side, stale tax for the session is preferable to
 *     a flicker between fetches.
 */
const taxConfigCache = new Map<string, TaxConfig>();
const taxConfigInFlight = new Map<string, Promise<TaxConfig | null>>();

async function loadTaxConfig(
  domain: string,
  signal?: AbortSignal,
): Promise<TaxConfig | null> {
  const cached = taxConfigCache.get(domain);
  if (cached) return cached;

  const inflight = taxConfigInFlight.get(domain);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      const { data } = await api.get('/tenant/tax-config', {
        params: { domain },
        signal,
      });
      if (data?.taxConfig) {
        const config: TaxConfig = {
          rate: Number(data.taxConfig.rate) || 0,
          label: data.taxConfig.label || 'Tax',
          inclusive: data.taxConfig.inclusive === true,
        };
        taxConfigCache.set(domain, config);
        return config;
      }
      return null;
    } catch (err) {
      // Network failure — don't cache; let the next mount retry.
      return null;
    } finally {
      taxConfigInFlight.delete(domain);
    }
  })();

  taxConfigInFlight.set(domain, promise);
  return promise;
}
const SHIPPING_THRESHOLD = 10000;
const SHIPPING_COST = 1000;

const DEFAULT_TAX: TaxConfig = { rate: 0, label: 'Tax', inclusive: false };

const CartContext = createContext<CartContextType | undefined>(undefined);

// Helper function to calculate item price including modifiers + line discount
const calculateItemPrice = (item: CartItem): number => {
  if (item.isGift) return 0;
  const basePrice = Number(item.product.price) || 0;

  // Calculate total modifier from selected options
  let totalModifier = 0;
  if (item.selectedOptions) {
    // selectedOptions can be either { options: [...] } or directly an array
    const optionsArray = item.selectedOptions.options || item.selectedOptions;
    if (Array.isArray(optionsArray)) {
      totalModifier = optionsArray.reduce((sum: number, option: any) => {
        return sum + (Number(option.priceModifier) || 0);
      }, 0);
    }
  }

  let price = basePrice + (totalModifier / 100);
  if (item.lineDiscount) {
    const { percentOff, amountOff } = item.lineDiscount;
    if (typeof percentOff === 'number' && percentOff > 0) {
      price = price * (1 - Math.min(100, percentOff) / 100);
    }
    if (typeof amountOff === 'number' && amountOff > 0) {
      price = price - amountOff;
    }
  }
  return Math.max(0, price);
};

const calculateTotals = (
  items: CartItem[],
  tax: TaxConfig,
  bundleOffers: BundleOffer[] = [],
): Pick<
  CartState,
  'itemCount' | 'subtotal' | 'tax' | 'shipping' | 'total' | 'bundleSavings' | 'appliedBundles'
> => {
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const grossSubtotal = items.reduce((sum, item) => {
    const itemPrice = calculateItemPrice(item);
    return sum + (itemPrice * item.quantity);
  }, 0);

  // Merchant bundle offers ("buy these together for RM50"). Computed here so
  // the drawer, cart page and checkout all quote the same number the backend
  // will charge — it re-derives this from the same config at checkout.
  const bundle = applyBundleOffers(
    items
      .filter((item) => !item.isGift)
      .map((item) => ({
        productId: Number(item.product?.id),
        quantity: item.quantity,
        unitPriceInt: Math.round(calculateItemPrice(item) * 100),
      }))
      .filter((line) => Number.isFinite(line.productId) && line.productId > 0),
    bundleOffers,
  );
  const bundleSavings = bundle.savingsInt / 100;
  const subtotal = Math.max(0, grossSubtotal - bundleSavings);

  // Inclusive: subtotal already contains tax — display tax portion, total = subtotal.
  // Exclusive: tax is added on top.
  const taxAmount = tax.inclusive
    ? subtotal - subtotal / (1 + tax.rate)
    : subtotal * tax.rate;
  const shipping = 0;
  const total = tax.inclusive ? subtotal + shipping : subtotal + taxAmount + shipping;

  return {
    itemCount,
    subtotal,
    tax: taxAmount,
    shipping,
    total,
    bundleSavings,
    appliedBundles: bundle.applied.map(({ id, name, sets }) => ({ id, name, sets })),
  };
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [lastAddedProduct, setLastAddedProduct] = useState<{ name: string; image?: string; quantity: number } | null>(null);
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX);
  const [bundleOffers, setBundleOffers] = useState<BundleOffer[]>(() =>
    getBundleOffersSync(),
  );

  // Bundle offers ride along with the storefront config. Seeded synchronously
  // above when that config is already cached (the common case after the first
  // page), and loaded here on a cold start so totals don't quote list price
  // for a beat before the discount lands.
  useEffect(() => {
    let alive = true;
    if (bundleOffers.length > 0) return;
    loadBundleOffers()
      .then((offers) => {
        if (alive && offers.length > 0) setBundleOffers(offers);
      })
      .catch(() => {
        // No offers is a valid state — cart just shows list prices.
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch tax config once per tenant per browser session. The module-scope
  // cache above coalesces concurrent mounts and persists across re-mounts
  // (route nav) so we don't spam `/tenant/tax-config` on every page change.
  useEffect(() => {
    const controller = new AbortController();
    const domain =
      typeof window !== 'undefined' ? window.location.hostname : '';
    if (!domain) return () => controller.abort();

    // Hit cache synchronously to avoid a no-op render cycle when warm.
    const cached = taxConfigCache.get(domain);
    if (cached) {
      setTaxConfig(cached);
      return () => controller.abort();
    }

    loadTaxConfig(domain, controller.signal)
      .then((config) => {
        if (controller.signal.aborted || !config) return;
        setTaxConfig(config);
      })
      .catch(() => {
        // Errors fall back to DEFAULT_TAX (0%) — caller has nothing to do.
      });

    return () => controller.abort();
  }, []);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = tenantStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        // Convert date strings back to Date objects
        const items = parsed.map((item: any) => ({
          ...item,
          addedAt: new Date(item.addedAt),
        }));
        setCartItems(items);
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      tenantStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cartItems]);

  const addItem = useCallback((rawProduct: Product, quantity: number = 1, options?: Record<string, any>, meta?: AddItemMeta) => {
    // Defensively sanitize product data
    const product: Product = {
      ...rawProduct,
      name: typeof rawProduct.name === 'string' ? rawProduct.name : JSON.stringify(rawProduct.name || ''),
      description: typeof rawProduct.description === 'string' ? rawProduct.description : '',
      price: Number(rawProduct.price) || 0,
      // Handle images: ensure it's an array of strings, or empty array
      images: Array.isArray(rawProduct.images) ? rawProduct.images : (rawProduct.imageUrl ? (Array.isArray(rawProduct.imageUrl) ? rawProduct.imageUrl : [rawProduct.imageUrl]) : []),
      // Handle imageUrl: ensure string or undefined
      imageUrl: typeof rawProduct.imageUrl === 'string' ? rawProduct.imageUrl : undefined,
      options: Array.isArray(rawProduct.options) ? rawProduct.options : [],
    };

    setCartItems((prevItems) => {
      // Items with discount/gift/bundle metadata must never merge with plain items
      const hasMeta = !!(meta && (meta.lineDiscount || meta.isGift || meta.bundleId || meta.giftKey));

      // Check if item already exists (only merge plain items with same options)
      const existingItemIndex = hasMeta
        ? -1
        : prevItems.findIndex(
            (item) =>
              item.product.id === product.id &&
              !item.lineDiscount &&
              !item.isGift &&
              !item.bundleId &&
              JSON.stringify(item.selectedOptions) === JSON.stringify(options)
          );

      if (existingItemIndex > -1) {
        // Update quantity of existing item
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity,
        };
        return updatedItems;
      } else {
        // Add new item
        const newItem: CartItem = {
          id: `${product.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          product,
          quantity,
          selectedOptions: options,
          addedAt: new Date(),
          lineDiscount: meta?.lineDiscount,
          isGift: meta?.isGift,
          bundleId: meta?.bundleId,
          giftKey: meta?.giftKey,
        };
        return [...prevItems, newItem];
      }
    });

    // Track Facebook Pixel AddToCart event if enabled
    if (facebookPixel.isInitialized()) {
      const eventSettings = (window as any).__FB_PIXEL_EVENT_SETTINGS__ || {};
      const isAddToCartEnabled = eventSettings.addToCart !== false; // Default to true

      if (isAddToCartEnabled) {
        const itemPrice = Number(product.price) || 0;
        facebookPixel.addToCart({
          content_ids: [String(product.id)],
          content_name: product.name,
          content_type: 'product',
          value: itemPrice * quantity,
          currency: ((product as any)?.currency || 'MYR').toString().toUpperCase(),
          quantity: quantity,
        });
      }
    }

    // Show modal after adding item (skip for silent/auto-injected items like GWP gifts)
    if (!meta?.silent) {
      const productImage = product.imageUrl || (Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : undefined);
      setLastAddedProduct({
        name: product.name,
        image: typeof productImage === 'string' ? productImage : undefined,
        quantity,
      });
      setShowModal(true);
    }
  }, []);

  const removeItem = useCallback((itemId: string) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  }, []);

  const removeByBundleId = useCallback((bundleId: string) => {
    if (!bundleId) return;
    setCartItems((prevItems) => prevItems.filter((item) => item.bundleId !== bundleId));
  }, []);

  const removeByGiftKey = useCallback((giftKey: string) => {
    if (!giftKey) return;
    setCartItems((prevItems) => prevItems.filter((item) => item.giftKey !== giftKey));
  }, []);

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const isInCart = useCallback(
    (productId: string): boolean => {
      return cartItems.some((item) => String(item.product.id) === String(productId));
    },
    [cartItems]
  );

  const getItemQuantity = useCallback(
    (productId: string): number => {
      const item = cartItems.find((item) => String(item.product.id) === String(productId));
      return item ? item.quantity : 0;
    },
    [cartItems]
  );

  const totals = useMemo(
    () => calculateTotals(cartItems, taxConfig, bundleOffers),
    [cartItems, taxConfig, bundleOffers],
  );

  const cart: CartState = {
    items: cartItems,
    ...totals,
  };

  const value: CartContextType = {
    cart,
    items: cart.items,
    itemCount: cart.itemCount,
    taxConfig,
    addItem,
    removeItem,
    removeByBundleId,
    removeByGiftKey,
    updateQuantity,
    clearCart,
    isInCart,
    getItemQuantity,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
      {lastAddedProduct && (
        <AddToCartModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          productName={lastAddedProduct.name}
          productImage={lastAddedProduct.image}
          quantity={lastAddedProduct.quantity}
        />
      )}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
